import { ComponentType, FC } from "react";

import { usePortalCommunicationMessageForm } from "../Hooks/usePortalCommunicationMessageForm";

interface WithPortalCommunicationMessageFormProps {
    shouldValidatePatient?: boolean;
}

/**
 * A higher-order component (HOC) that injects portal communication message form logic
 * into the wrapped component. This HOC utilizes the `usePortalCommunicationMessageForm` hook
 * to provide additional functionality to the wrapped component.
 *
 * @template P - The props type of the wrapped component.
 *
 * @param WrappedComponent - The component to be wrapped and enhanced with portal communication
 * message form functionality.
 *
 * @returns A functional component that wraps the provided component and injects the
 * portal communication message form logic.
 *
 * @remarks
 * - The `shouldValidatePatient` prop is used to determine whether patient validation
 * should be performed in the `usePortalCommunicationMessageForm` hook.
 * - The HOC passes down all other props (`restProps`) to the wrapped component.
 * - The display name of the HOC is dynamically set based on the display name or name
 * of the wrapped component for easier debugging.
 *
 * @example
 * ```tsx
 * import React from 'react';
 * import { withPortalCommunicationMessageForm } from './withPortalCommunicationMessageForm';
 *
 * const MyComponent = ({ someProp, portalCommunicationMessageForm }) => {
 *     // Use the injected portalCommunicationMessageForm logic here
 *     return <div>{someProp}</div>;
 * };
 *
 * export default withPortalCommunicationMessageForm(MyComponent);
 * ```
 */
function withPortalCommunicationMessageForm<P extends object>(
    WrappedComponent: ComponentType<P & ReturnType<typeof usePortalCommunicationMessageForm>>
): FC<P & WithPortalCommunicationMessageFormProps> {
    function HighOrderComponent(props: P & WithPortalCommunicationMessageFormProps): JSX.Element {
        const {
            shouldValidatePatient = false,
            ...restProps
        } = props;

        const portalCommunicationMessageForm = usePortalCommunicationMessageForm(shouldValidatePatient);

        return (
            <WrappedComponent
                {...(restProps as P)}
                {...portalCommunicationMessageForm}
            />
        );
    }

    return HighOrderComponent;
}

export { withPortalCommunicationMessageForm };
export type { WithPortalCommunicationMessageFormProps };
